import React, { useContext, useMemo, useState } from 'react';
import { Button, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { PluginProps } from '../Plugin';
import ImageHotspots from '../../../../app/pages/InteractiveTest/components/ImageHotspots';
import ImageClickHotspots from '../../../../app/pages/InteractiveTest/components/ImageClickHotspots';
import ImageHotspotsDragNDrop from '../../../../app/pages/InteractiveTest/components/ImageHotspotsDnd';
import { AuthContext, Role } from '../../../../../context/AuthProvider';
import { useQueryParam } from '../../../../../hooks/useQueryParams';
import { MD5 } from '../../../../../helpers/utils';
import {
  useInteractiveComponentStateQuery,
  useSaveInteractiveComponentStateMutation,
} from '../../../../../generated/graphqlModels';
import SelectUser from '../../SelectUser';
import { GET_INTERACTIVE_COMPONENT_STATE } from '../../../../app/pages/InteractiveTest/components/shared/queries';
import { useIsMobile } from '../../../../../hooks/isMobile';

const ImageHotspotsPlugin = (props: PluginProps) => {
  const { content, studyGroup } = props;
  const { t } = useTranslation();
  const contentData = content.data;
  const user = useContext(AuthContext);
  const debugParam = useQueryParam('debug');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(
    user?.pk || null,
  );
  const [showCorrect, setShowCorrect] = useState(false);

  const debug = user?.roles.includes(Role.staff) && !!debugParam;
  const type = contentData.type;

  const studyGroupId = studyGroup.pk;

  const queryVariables = {
    userId: selectedUserId! || user?.pk!,
    studyGroupId: studyGroupId!,
    componentId: contentData.unique_id,
  };
  const isMobile = useIsMobile();
  const {
    data,
    // loading
  } = useInteractiveComponentStateQuery({
    variables: queryVariables,
    skip: user == null || user.pk == null || studyGroupId == null,
  });

  const [
    saveState,
    // { data: mutData, loading: mutLoading, error: mutError }
  ] = useSaveInteractiveComponentStateMutation({
    refetchQueries: [
      {
        query: GET_INTERACTIVE_COMPONENT_STATE,
        variables: queryVariables,
      },
    ],
  });

  const hash = useMemo(() => {
    return MD5.hash(JSON.stringify(contentData.config));
  }, [contentData.config]);

  const submit = (incorrect: number, total: number, state: any) => {
    saveState({
      variables: {
        data: {
          componentId: contentData.unique_id,
          userId: user!.pk,
          studyGroupId: studyGroupId!,
          hash,
          state,
        },
        reset: false,
      },
    });
  };

  const reset = () => {
    saveState({
      variables: {
        data: {
          componentId: contentData.unique_id,
          userId: user!.pk,
          studyGroupId: studyGroupId!,
          hash,
          state: '',
        },
        reset: true,
      },
    });
  };

  const isViewOnly = !!selectedUserId && selectedUserId !== user?.pk!;

  const ShowCorrect = () =>
    user?.roles.includes(Role.teacher) ? (
      <Button onClick={() => setShowCorrect(!showCorrect)}>
        {showCorrect ? t('plugin.hideCorrect') : t('plugin.showCorrect')}
      </Button>
    ) : null;

  if (type === 'info') {
    return (
      <>
        <ImageHotspots
          key={contentData.id}
          spots={contentData.config}
          src={contentData.imageSrc || '/static/ui/image-hotspots.jpg'}
          debug={debug}
          showCorrect={showCorrect}
        />
      </>
    );
  }
  if (type === 'click') {
    return (
      <>
        <SelectUser
          studyGroupId={studyGroupId}
          studentId={selectedUserId}
          setStudentId={setSelectedUserId}
        />
        <div
          style={
            isMobile
              ? {
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '10px',
                }
              : undefined
          }
        >
          <ImageClickHotspots
            key={contentData.id}
            spots={contentData.config}
            src={contentData.imageSrc || '/static/ui/image-hotspots.jpg'}
            debug={debug}
            showCorrect={showCorrect}
            submit={submit}
            reset={reset}
            initialState={data?.getInteractiveContentState?.state}
            isViewOnly={isViewOnly}
            style={{ margin: '10px 0' }}
          />
          <ShowCorrect />
        </div>
      </>
    );
  }
  if (type === 'dnd') {
    return (
      <>
        <SelectUser
          studyGroupId={studyGroupId}
          studentId={selectedUserId}
          setStudentId={setSelectedUserId}
        />
        <div
          style={
            isMobile
              ? {
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '10px',
                }
              : undefined
          }
        >
          <ImageHotspotsDragNDrop
            key={contentData.id}
            spots={contentData.config}
            src={contentData.imageSrc || '/static/ui/image-hotspots.jpg'}
            dndPosition={isMobile ? 'bottom' : contentData.dnd_position}
            debug={debug}
            showCorrect={showCorrect}
            submit={submit}
            reset={reset}
            initialState={data?.getInteractiveContentState?.state}
            isViewOnly={isViewOnly}
            style={{ margin: '10px 0' }}
          />
          <ShowCorrect />
        </div>
      </>
    );
  }
  return <Text>{t('plugin.imageHotspots.unknownType', { type })}</Text>;
};

export default ImageHotspotsPlugin;
